import TableHeader from "../customTable/TableHeader";
import TableRow from "../customTable/TableRow";
import Pagination from "../customTable/Pagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import CustomSpinner from "../spinner/CustomSpinner";
import { limitNumberWithinRange } from "../../services/format/range";
import { sortArrayOfObjects } from "../../services/array/sortArray";
import { sortingDirection, SortingDirection } from "../../app/constants/sortingData";
import NoTableContent from "../customTable/NoTableContent";
import { useTranslation } from "react-i18next";
import { CustomTableWithFrontendProps } from "./CustomTableWithFrontendProps";
import "../customTable/CustomTable.scss";

const CustomTableWithFrontend = <T,>({
    data,
    columns,
    id,
    isSorting,
    isPagination,
    isLoading,
    minWidth,
    enableRowSelection = false,
    isRowSelectable,
    onSelectedRowChange,
    defaultSort,
}: CustomTableWithFrontendProps<T>) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ columnKey: string | null; order: SortingDirection }>(
        () => ({
            columnKey: defaultSort?.columnKey ?? null,
            order: defaultSort?.order ?? sortingDirection.none,
        })
    );
    const [dataLength, setDataLength] = useState(data.length);
    const [selectedRow, setSelectedRow] = useState<T | null>(null);
    const hasLoadedOnce = useRef(false);

    if (!isLoading) {
        hasLoadedOnce.current = true;
    }

    useEffect(() => {
        setPageSize(limitNumberWithinRange(data.length, data.length, 5));
    }, [data]);

    const currentTableData = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        const lastPageIndex = firstPageIndex + pageSize;

        setDataLength(data.length);

        //Sorting
        const sortedData =
            sortConfig.columnKey !== null
                ? sortArrayOfObjects(data, sortConfig.columnKey as keyof T, sortConfig.order)
                : data;
        //Pagination
        return sortedData.slice(firstPageIndex, lastPageIndex);
    }, [currentPage, data, pageSize, sortConfig.columnKey, sortConfig.order]);

    const sortedColumnIndex = useMemo(() => {
        if (!sortConfig.columnKey) return null;
        const idx = columns.findIndex((c) => c.key === sortConfig.columnKey);
        return idx >= 0 ? idx : null;
    }, [columns, sortConfig.columnKey]);

    const handleSort = useCallback(
        (columnIndex: number, order: "asc" | "desc") => {
            const columnKey = columns[columnIndex]?.key;
            if (!columnKey) return;
            setSortConfig({ columnKey, order });
            setCurrentPage(1);
        },
        [columns]
    );

    const handleRowSelect = useCallback(
        (item: T, isSelected: boolean) => {
            const newSelectedRow = isSelected ? item : null;
            setSelectedRow(newSelectedRow);

            if (onSelectedRowChange) {
                onSelectedRowChange(newSelectedRow);
            }
        },
        [onSelectedRowChange]
    );

    return (
        <>
            <Table responsive striped id={id} className="custom-table" style={{ minWidth: minWidth }}>
                <thead>
                    <TableHeader
                        columns={columns}
                        onSort={isSorting ? handleSort : undefined}
                        sortedColumnIndex={sortedColumnIndex}
                        sortOrder={sortConfig.order === sortingDirection.none ? undefined : (sortConfig.order as "asc" | "desc")}
                        enableRowSelection={enableRowSelection}
                    />
                </thead>
                <tbody>
                    {isLoading || (!hasLoadedOnce.current && data.length === 0) ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center w-100">
                                <CustomSpinner hideTextTitle={true} sizeRem={6} />
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <NoTableContent title={t("tables.noData")} columnCount={columns.length + (enableRowSelection ? 1 : 0)} />
                    ) : (
                        <TableRow
                            data={currentTableData}
                            columns={columns}
                            enableRowSelection={enableRowSelection}
                            isRowSelectable={isRowSelectable}
                            onRowSelect={(item, isSelected) => handleRowSelect(item, isSelected)}
                            selectedRow={selectedRow}
                        />
                    )}
                </tbody>
            </Table>
            {isPagination && (
                <Pagination
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalCount={dataLength}
                    onPageChange={(page) => setCurrentPage(page)}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    }}
                />
            )}
        </>
    );
};

export default CustomTableWithFrontend;
